import { useEffect, useState, useCallback } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
} from 'recharts'
import { useAuth } from '../auth'
import { useLang } from '../i18n'
import { usePrefs } from '../prefs'
import { getChartColors } from '../theme/chartPalette'

// ── Types ──────────────────────────────────────────────────────────────────────

type Dimension = 'task' | 'region'
type Granularity = 'month' | 'quarter' | 'year'

interface TrendPoint {
  period: string
  label: string
  volume: number
}

interface TrendRow {
  period: string
  label: string
  volume: number
  mom_pct: number | null
  yoy_pct: number | null
  yoy_insufficient: boolean
}

interface TrendsData {
  dimension: Dimension
  value: string
  granularity: Granularity
  points: TrendPoint[]
  table: TrendRow[]
  range: { from: string; to: string }
}

interface TaskItem {
  id: string
  name: string
  category_code: string
  is_active: boolean
  deleted_at: string | null
}

interface CategoryItem {
  id: string
  code: string
  display_name_en: string
  display_name_cn: string
  tasks: TaskItem[]
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function authHeader(token: string | null): Record<string, string> {
  if (!token) return {}
  return { Authorization: `Bearer ${token}` }
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

function firstOfYearStr(): string {
  return `${new Date().getFullYear()}-01-01`
}

function fmtPct(pct: number | null): string {
  if (pct === null) return '—'
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(1)}%`
}

// ── Component ──────────────────────────────────────────────────────────────────

export function TrendAnalysisPage() {
  const { accessToken, user } = useAuth()
  const { t } = useLang()
  const { theme } = usePrefs()
  const colors = getChartColors(theme)

  const role = user?.role ?? ''

  // ── Filters state ────────────────────────────────────────────────────────────
  const [dimension, setDimension] = useState<Dimension>('task')
  const [taskValue, setTaskValue] = useState<string>('')
  const [regionValue, setRegionValue] = useState<'CN' | 'US'>('CN')
  const [fromDate, setFromDate] = useState<string>(firstOfYearStr())
  const [toDate, setToDate] = useState<string>(todayStr())
  const [granularity, setGranularity] = useState<Granularity>('month')

  // ── Category / task list ──────────────────────────────────────────────────────
  const [allTasks, setAllTasks] = useState<TaskItem[]>([])

  useEffect(() => {
    if (!accessToken || role === 'member') return
    fetch('/api/categories', { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((r) => r.json())
      .then((resp: { data: CategoryItem[] }) => {
        const cats = resp.data ?? []
        const tasks = cats.flatMap((c) =>
          c.tasks.filter((t) => t.is_active && !t.deleted_at)
        )
        setAllTasks(tasks)
      })
      .catch(() => {})
  }, [role, accessToken])

  // Set default task selection once tasks are loaded
  useEffect(() => {
    if (allTasks.length > 0 && !taskValue) {
      setTaskValue(allTasks[0].id)
    }
  }, [allTasks, taskValue])

  // ── Trends data ───────────────────────────────────────────────────────────────
  const [data, setData] = useState<TrendsData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchTrends = useCallback(() => {
    const value = dimension === 'task' ? taskValue : regionValue
    if (!accessToken || role === 'member') return
    if (!value || !fromDate || !toDate) return

    const params = new URLSearchParams({
      dimension,
      value,
      from: fromDate,
      to: toDate,
      granularity,
    })

    setLoading(true)
    setError(null)
    fetch(`/api/trends?${params.toString()}`, { headers: authHeader(accessToken) })
      .then((r) => r.json())
      .then((resp) => {
        if (resp.error) {
          setError(resp.error.message ?? t('errorGeneric'))
          setData(null)
        } else {
          setData(resp.data ?? null)
        }
      })
      .catch(() => setError(t('errorGeneric')))
      .finally(() => setLoading(false))
  }, [accessToken, role, dimension, taskValue, regionValue, fromDate, toDate, granularity, t])

  useEffect(() => {
    if (dimension === 'task' && !taskValue) return
    fetchTrends()
  }, [fetchTrends, dimension, taskValue])

  // ── Member guard ──────────────────────────────────────────────────────────────
  if (role === 'member') {
    return (
      <div style={{ padding: '2rem', color: 'var(--color-text)' }}>
        {t('trendForbidden')}
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div
      data-testid="trend-analysis-page"
      style={{ padding: '1rem', color: 'var(--color-text)', fontFamily: 'inherit' }}
    >
      {/* ── Filter bar ── */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem',
          alignItems: 'flex-end',
          marginBottom: '1.25rem',
          padding: '0.75rem 1rem',
          background: 'var(--color-card, #f9f9f9)',
          borderRadius: '8px',
          border: '1px solid var(--color-border, #e0e0e0)',
        }}
      >
        {/* Dimension selector */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.85rem' }}>
          <span>{t('trendDimension')}</span>
          <select
            data-testid="dimension-select"
            value={dimension}
            onChange={(e) => setDimension(e.target.value as Dimension)}
            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--color-border, #ccc)' }}
          >
            <option value="task">{t('trendDimTask')}</option>
            <option value="region">{t('trendDimRegion')}</option>
          </select>
        </label>

        {/* Value selector */}
        {dimension === 'task' ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.85rem' }}>
            <span>{t('trendTask')}</span>
            <select
              data-testid="task-select"
              value={taskValue}
              onChange={(e) => setTaskValue(e.target.value)}
              style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--color-border, #ccc)', minWidth: '160px' }}
            >
              {allTasks.length === 0 && (
                <option value="">{t('loading')}</option>
              )}
              {allTasks.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.85rem' }}>
            <span>{t('trendRegion')}</span>
            <select
              data-testid="region-select"
              value={regionValue}
              onChange={(e) => setRegionValue(e.target.value as 'CN' | 'US')}
              style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--color-border, #ccc)' }}
            >
              <option value="CN">CN</option>
              <option value="US">US</option>
            </select>
          </label>
        )}

        {/* Date range */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.85rem' }}>
          <span>{t('timeRangeFrom')}</span>
          <input
            data-testid="from-date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--color-border, #ccc)' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.85rem' }}>
          <span>{t('timeRangeTo')}</span>
          <input
            data-testid="to-date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--color-border, #ccc)' }}
          />
        </label>

        {/* Granularity */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.85rem' }}>
          <span>{t('trendGranularity')}</span>
          <select
            data-testid="granularity-select"
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as Granularity)}
            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--color-border, #ccc)' }}
          >
            <option value="month">{t('trendGranMonth')}</option>
            <option value="quarter">{t('trendGranQuarter')}</option>
            <option value="year">{t('trendGranYear')}</option>
          </select>
        </label>

        {/* Query button */}
        <button
          data-testid="query-btn"
          onClick={fetchTrends}
          style={{
            padding: '5px 16px',
            borderRadius: '4px',
            border: 'none',
            background: '#4a90d9',
            color: '#fff',
            cursor: 'pointer',
            alignSelf: 'flex-end',
          }}
        >
          {t('trendQuery')}
        </button>
      </div>

      {/* ── Loading / Error ── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem', color: colors.axisColor }}>
          {t('loading')}
        </div>
      )}
      {error && !loading && (
        <div style={{ color: '#d9534f', padding: '0.5rem 0' }}>{error}</div>
      )}

      {/* ── Chart ── */}
      {!loading && data && data.points.length > 0 && (
        <div
          data-testid="trend-chart"
          style={{
            background: 'var(--color-card, #fff)',
            borderRadius: '8px',
            border: '1px solid var(--color-border, #e0e0e0)',
            padding: '1rem',
            marginBottom: '1.25rem',
          }}
        >
          <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>{t('trendChartTitle')}</div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.points} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.gridColor} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: colors.axisColor }}
                stroke={colors.axisColor}
              />
              <YAxis
                tick={{ fontSize: 12, fill: colors.axisColor }}
                stroke={colors.axisColor}
              />
              <ReTooltip
                contentStyle={{ background: colors.tooltipBg, color: colors.tooltipText, borderRadius: '6px' }}
              />
              <Line
                type="monotone"
                dataKey="volume"
                stroke={colors.categoryColors[0]}
                strokeWidth={2}
                dot={{ r: 4, fill: colors.categoryColors[0] }}
                activeDot={{ r: 6 }}
                name={t('trendVolume')}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {!loading && data && data.points.length === 0 && !error && (
        <div style={{ color: colors.axisColor, padding: '1rem 0' }}>{t('trendNoData')}</div>
      )}

      {/* ── Table ── */}
      {!loading && data && data.table.length > 0 && (
        <div
          data-testid="trend-table"
          style={{
            background: 'var(--color-card, #fff)',
            borderRadius: '8px',
            border: '1px solid var(--color-border, #e0e0e0)',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: 'var(--color-table-header, #f0f0f0)' }}>
                <th style={thStyle}>{t('trendPeriod')}</th>
                <th style={thStyle}>{t('trendVolume')}</th>
                <th style={thStyle}>{t('trendMom')}</th>
                <th style={thStyle}>{t('trendYoy')}</th>
              </tr>
            </thead>
            <tbody>
              {data.table.map((row) => (
                <tr key={row.period} data-testid={`trend-row-${row.period}`} style={{ borderTop: '1px solid var(--color-border, #e0e0e0)' }}>
                  <td style={tdStyle}>{row.label}</td>
                  <td style={tdStyle}>{row.volume}</td>
                  <td style={{ ...tdStyle, ...pctStyle(row.mom_pct) }}>
                    {row.mom_pct === null ? '—' : fmtPct(row.mom_pct)}
                  </td>
                  <td style={{ ...tdStyle, ...pctStyle(row.yoy_pct) }}>
                    {row.yoy_insufficient
                      ? <span data-testid="yoy-insufficient">{t('trendYoyInsufficient')}</span>
                      : fmtPct(row.yoy_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Style helpers ─────────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  fontWeight: 600,
  whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '6px 12px',
  whiteSpace: 'nowrap',
}

function pctStyle(pct: number | null): React.CSSProperties {
  if (pct === null) return {}
  if (pct > 0) return { color: '#5cb85c', fontWeight: 600 }
  if (pct < 0) return { color: '#d9534f', fontWeight: 600 }
  return {}
}
